import { useCallback, useEffect, useMemo, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { readRememberedMediaFile, rememberMediaFiles } from "../common/mediaFileCache";
import fetchMediaFile from "../services/fetchMediaFile";
const mediaFileDetailsLogger = appLogger.withTag("MediaFileDetails");
const idleMediaFileDetailsState = { context: null, viewModel: { status: "idle" } };
function isSameRequestContext(previous, current) {
    if (previous === null || current === null) {
        return previous === current;
    }
    return previous.id === current.id;
}
function buildInitialMediaFileDetailsState(context) {
    if (context === null) {
        return idleMediaFileDetailsState;
    }
    // A surface that already listed this file hands it over, so the first paint is the file rather than a spinner.
    const remembered = readRememberedMediaFile(context.id);
    return { context, viewModel: remembered ? { status: "ready", file: remembered } : { status: "loading" } };
}
/**
 * Request-scoped single-file loader shared by the standalone page and the
 * details modal. Failed loads surface as view-model status — they never throw.
 */
export default function useMediaFileDetails({ id, isEnabled = true }) {
    const currentContext = useMemo(() => (isEnabled && id ? { id } : null), [isEnabled, id]);
    const [stateMediaFileDetails, setStateMediaFileDetails] = useState(() => buildInitialMediaFileDetailsState(currentContext));
    const [stateManualRetryCount, setStateManualRetryCount] = useState(0);
    const retry = useCallback(() => {
        setStateManualRetryCount((manualRetryCount) => manualRetryCount + 1);
    }, []);
    if (!isSameRequestContext(stateMediaFileDetails.context, currentContext)) {
        // Render-phase reset so a stale view model is never committed.
        setStateMediaFileDetails(buildInitialMediaFileDetailsState(currentContext));
    }
    useEffect(() => {
        if (currentContext === null) {
            return undefined;
        }
        const requestContext = currentContext;
        // Nothing to wait for: the opener already had this file, and a retry is the only way back here.
        if (stateManualRetryCount === 0 && readRememberedMediaFile(requestContext.id)) {
            return undefined;
        }
        const abortController = new AbortController();
        // Context changes already reset via render-phase state above; this also
        // covers manual retry, which reuses the same context.
        setStateMediaFileDetails(buildInitialMediaFileDetailsState(requestContext));
        void fetchMediaFile({ id: requestContext.id, signal: abortController.signal })
            .then((result) => {
            if (abortController.signal.aborted || result.status === "cancelled") {
                return;
            }
            if (result.status === "success") {
                rememberMediaFiles([result.file]);
                setStateMediaFileDetails({ context: requestContext, viewModel: { status: "ready", file: result.file } });
                return;
            }
            if (result.status === "not-found") {
                setStateMediaFileDetails({ context: requestContext, viewModel: { status: "not-found" } });
                return;
            }
            mediaFileDetailsLogger.error("Media file request failed", {
                id: requestContext.id,
                category: result.category,
                httpStatus: result.httpStatus,
            });
            setStateMediaFileDetails({ context: requestContext, viewModel: { status: "error", retry } });
        })
            .catch((unexpectedError) => {
            if (abortController.signal.aborted) {
                return;
            }
            mediaFileDetailsLogger.error("Media file request handling failed", {
                id: requestContext.id,
                unexpectedError,
            });
            setStateMediaFileDetails({ context: requestContext, viewModel: { status: "error", retry } });
        });
        return () => {
            abortController.abort();
        };
    }, [currentContext, stateManualRetryCount, retry]);
    return stateMediaFileDetails.viewModel;
}
